import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import AdminTabsNav from "@/components/AdminTabsNav";
import { useWallet } from "@/context/WalletContext";
import { useCurrency } from "@/context/CurrencyContext";
import {
  Bot,
  Vault,
  RefreshCw,
  ShieldAlert,
  Loader2,
  Copy,
  Send,
  Check,
  X,
  TrendingUp,
  TrendingDown,
  Pause,
  Play,
  DollarSign,
  ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtMicro(v) {
  const n = (Number(v) || 0) / 1_000_000;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtPct(v) {
  const n = Number(v) || 0;
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}
function trunc(a, n = 8) {
  if (!a) return "—";
  return `${a.slice(0, n)}…${a.slice(-4)}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return "—";
  }
}
async function copyText(t) {
  try {
    await navigator.clipboard.writeText(t);
    toast.success("Copied");
  } catch {
    /* noop */
  }
}

/**
 * AdminBots — Admin → Bots tab. Mirrors Admin Perps.
 *  1. Bots House Treasury (sweep to WSY, reconcile)
 *  2. Totals strip (win/loss risk, suspicious, pending reviews)
 *  3. Manual-review queue for frozen (guardrail-tripped) withdrawals
 *  4. Live risk board of active bots
 *  5. Closed bots ledger
 */
export default function AdminBots() {
  const { getAuthHeaders, isConnected } = useWallet();
  const { showUsd, stxoshiUsd } = useCurrency();
  // USD-aware amount formatter (shadows module fmtMicro) so the global toggle
  // flips Win/Loss risk and balances on the bots admin board.
  const fmtMicro = (v) => {
    const n = (Number(v) || 0) / 1_000_000;
    if (showUsd) {
      const usd = n * (stxoshiUsd || 0.0000037);
      return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: Math.abs(usd) < 0.01 ? 8 : 2 })}`;
    }
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepDest, setSweepDest] = useState("");
  const [sweeping, setSweeping] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [modifyAmt, setModifyAmt] = useState({});
  const [processing, setProcessing] = useState(null);
  const [acting, setActing] = useState(null);

  const botAction = async (bot, action) => {
    const confirms = {
      close: `Close bot "${bot.name || bot.id.slice(0, 8)}"? It will stop trading.`,
      hold: `Put bot "${bot.name || bot.id.slice(0, 8)}" ON HOLD? This freezes the owner's User Wallet withdrawals until released.`,
      release_hold: `Release the hold on "${bot.name || bot.id.slice(0, 8)}"? Owner withdrawals resume (unless blocked elsewhere).`,
      close_principal: `Close "${bot.name || bot.id.slice(0, 8)}" and return ONLY the principal to the owner (any gain is kept by the house)? This sends a REAL on-chain transfer.`,
    };
    if (!window.confirm(confirms[action] || "Proceed?")) return;
    setActing(bot.id + action);
    try {
      const res = await fetch(`${API}/api/admin/bots/${bot.id}/action`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        if (action === "close_principal") {
          toast.success(`Returned ${fmtMicro(j.returned_stxoshi)} STXOSHI (house kept ${fmtMicro(j.gain_kept_stxoshi)})`, {
            action: j.explorer_url ? { label: "View", onClick: () => window.open(j.explorer_url, "_blank") } : undefined,
          });
        } else {
          toast.success(`Bot ${action.replace("_", " ")} done`);
        }
      } else {
        toast.error(j.detail?.message || j.detail || j.message || "Action failed");
      }
    } catch {
      toast.error("Action error");
    } finally {
      setActing(null);
      load();
    }
  };

  const load = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [ov, rv] = await Promise.all([
        fetch(`${API}/api/admin/bots/overview`, { headers }),
        fetch(`${API}/api/admin/bots/reviews?status=pending`, { headers }),
      ]);
      if (ov.ok) setData(await ov.json());
      if (rv.ok) setReviews((await rv.json()).reviews || []);
    } catch (e) {
      console.error("admin bots load error", e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isConnected) {
      setLoading(false);
      return;
    }
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [isConnected, load]);

  const doSweep = async () => {
    setSweeping(true);
    try {
      const body = sweepDest ? { destination_address: sweepDest } : {};
      const res = await fetch(`${API}/api/admin/bots/treasury/sweep`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        toast.success(`Swept ${fmtMicro(j.swept_stxoshi)} STXOSHI`, {
          action: j.explorer_url
            ? { label: "View", onClick: () => window.open(j.explorer_url, "_blank") }
            : undefined,
        });
      } else {
        toast.error(j.detail?.message || j.message || "Sweep failed — balance restored");
      }
    } catch (e) {
      toast.error("Sweep error");
    } finally {
      setSweeping(false);
      setSweepOpen(false);
      load();
    }
  };

  const doBackfillClamp = async () => {
    if (!window.confirm("Re-clamp ALL bots to the 25% APY band now? Corrects any over-band P&L/balance in bulk.")) return;
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/admin/bots/backfill-clamp`, { method: "POST", headers: getAuthHeaders() });
      const j = await res.json();
      if (res.ok) toast.success(`Re-clamped ${j.clamped} of ${j.scanned} bots`);
      else toast.error("Backfill failed");
    } catch { toast.error("Backfill error"); }
    finally { setReconciling(false); load(); }
  };

  const doReconcile = async () => {
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/admin/bots/treasury/reconcile`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const j = await res.json();
      if (res.ok) toast.success(`Booked ${j.bots_credited} bot losses (${fmtMicro(j.total_stxoshi)} STXOSHI)`);
      else toast.error("Reconcile failed");
    } catch {
      toast.error("Reconcile error");
    } finally {
      setReconciling(false);
      load();
    }
  };

  const doDriftReconcile = async () => {
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/admin/bots/drift/run`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const j = await res.json();
      if (res.ok) {
        toast.success(
          `Drift reconcile: healed ${j.healed || 0} bot(s) (${fmtMicro(j.healed_amount_stxoshi)}), flagged ${j.flagged || 0}`
        );
      } else {
        toast.error("Drift reconcile failed");
      }
    } catch {
      toast.error("Drift reconcile error");
    } finally {
      setReconciling(false);
      load();
    }
  };

  const processReview = async (review, action) => {
    if (action === "decline" && !window.confirm("Decline this withdrawal as an error?")) return;
    if ((action === "approve" || action === "modify") &&
      !window.confirm(
        `Send a REAL on-chain STXOSHI transfer to ${review.user_address}?`
      )) return;
    setProcessing(review.id + action);
    try {
      const payload = { action };
      if (action === "modify") payload.amount_stxoshi = parseFloat(modifyAmt[review.id]);
      const res = await fetch(`${API}/api/admin/bots/reviews/${review.id}/process`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        toast.success(
          action === "decline"
            ? "Withdrawal declined"
            : `Sent ${fmtMicro(j.sent_amount_stxoshi)} STXOSHI`,
          {
            action: j.explorer_url
              ? { label: "View", onClick: () => window.open(j.explorer_url, "_blank") }
              : undefined,
          }
        );
      } else {
        toast.error(j.detail?.message || j.detail || j.message || "Failed");
      }
    } catch {
      toast.error("Process error");
    } finally {
      setProcessing(null);
      load();
    }
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <Bot className="w-6 h-6 text-orange-500" /> Bots Admin
          </h1>
          <AdminTabsNav active="bots" />
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-12 text-center text-zinc-400" data-testid="admin-bots-connect">
              Connect your admin wallet to manage the Bots market.
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const t = data?.treasury || {};
  const totals = data?.totals || {};

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-bots-page">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <Bot className="w-6 h-6 text-orange-500" /> Bots Market Admin
        </h1>
        <AdminTabsNav active="bots" />

        {loading && !data ? (
          <div className="flex items-center gap-2 text-zinc-400 py-10">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Treasury */}
            <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="admin-bots-treasury">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Vault className="w-5 h-5 text-orange-500" /> Bots House Treasury
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-zinc-500">Sweepable Balance</p>
                    <p className="text-2xl font-bold text-orange-400" data-testid="bots-treasury-balance">
                      {fmtMicro(t.balance_stxoshi)} <span className="text-sm">STXOSHI</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Lifetime Losses Collected</p>
                    <p className="text-lg font-semibold">{fmtMicro(t.cumulative_losses)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Lifetime Swept</p>
                    <p className="text-lg font-semibold">{fmtMicro(t.cumulative_swept)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500 mb-3">
                  <span>Sweep destination:</span>
                  <button
                    className="font-mono text-zinc-300 inline-flex items-center gap-1 hover:text-white"
                    onClick={() => copyText(data?.sweep_destination)}
                  >
                    {trunc(data?.sweep_destination, 10)} <Copy className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => { setSweepDest(""); setSweepOpen(true); }}
                    disabled={!t.balance_stxoshi}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="bots-treasury-sweep-btn"
                  >
                    <Send className="w-4 h-4 mr-1" /> Sweep to Treasury Wallet
                  </Button>
                  <Button
                    variant="outline"
                    onClick={doReconcile}
                    disabled={reconciling}
                    className="border-zinc-700"
                    data-testid="bots-treasury-reconcile-btn"
                  >
                    {reconciling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Reconcile Losses
                  </Button>
                  <Button
                    variant="outline"
                    onClick={doBackfillClamp}
                    disabled={reconciling}
                    className="border-amber-500/40 text-amber-300"
                    data-testid="bots-backfill-clamp-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Re-clamp All (25% APY)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={doDriftReconcile}
                    disabled={reconciling}
                    className="border-purple-500/40 text-purple-300"
                    data-testid="bots-drift-reconcile-btn"
                  >
                    {reconciling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Run Drift Reconcile
                  </Button>
                </div>
                {data?.drift_last_run && (
                  <p className="text-[10px] text-zinc-500 mt-2" data-testid="bots-drift-lastrun">
                    Drift reconcile last ran {fmtDate(data.drift_last_run.ran_at)} — scanned{" "}
                    {data.drift_last_run.scanned || 0}, healed {data.drift_last_run.healed || 0}{" "}
                    ({fmtMicro(data.drift_last_run.healed_amount_stxoshi)}), flagged{" "}
                    {data.drift_last_run.flagged || 0}. Runs automatically every 24h.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Totals strip */}
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-4">
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Active Bots</p>
                <p className="text-xl font-bold" data-testid="bots-total-active">{totals.active_count || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Users' Winnings (risk)</p>
                <p className="text-xl font-bold text-green-400" data-testid="bots-total-win">{fmtMicro(totals.total_win_risk_stxoshi)}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Users' Losses (house)</p>
                <p className="text-xl font-bold text-red-400" data-testid="bots-total-loss">{fmtMicro(totals.total_loss_risk_stxoshi)}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Suspicious</p>
                <p className="text-xl font-bold text-amber-400" data-testid="bots-total-suspicious">{totals.suspicious_count || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Balance Drift</p>
                <p className="text-xl font-bold text-purple-400" data-testid="bots-total-drift">{totals.drift_flagged_count || 0}</p>
              </CardContent></Card>
              <Card className="bg-zinc-900 border-zinc-800"><CardContent className="p-3">
                <p className="text-xs text-zinc-500">Pending Reviews</p>
                <p className="text-xl font-bold text-orange-400" data-testid="bots-total-pending">{data?.pending_reviews || 0}</p>
              </CardContent></Card>
            </div>

            {/* Manual review queue */}
            <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="admin-bots-reviews">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShieldAlert className="w-5 h-5 text-amber-500" /> Withdrawal Reviews
                  <Badge variant="outline" className="ml-2">{reviews.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4" data-testid="bots-reviews-empty">
                    No withdrawals pending review.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {reviews.map((r) => (
                      <div key={r.id} className="border border-zinc-800 rounded-lg p-3" data-testid={`bots-review-row-${r.id}`}>
                        <div className="flex flex-wrap justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="font-semibold">{r.bot_name || r.bot_id} <span className="text-xs text-zinc-500">({r.bot_type})</span></p>
                            <button className="text-xs font-mono text-zinc-400 inline-flex items-center gap-1 hover:text-white"
                              onClick={() => copyText(r.user_address)}>
                              {trunc(r.user_address, 10)} <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-zinc-500">Requested</p>
                            <p className="font-bold text-orange-400">{fmtMicro(r.requested_amount_stxoshi)} STXOSHI</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                          <div><span className="text-zinc-500">Available</span><br/>{fmtMicro(r.available_balance_stxoshi)}</div>
                          <div><span className="text-zinc-500">P&L%</span><br/>{fmtPct(r.total_pnl_pct)}</div>
                          <div><span className="text-zinc-500">Days</span><br/>{(r.days_held || 0).toFixed(1)}</div>
                          <div><span className="text-zinc-500">Max cap</span><br/>{fmtMicro(r.max_withdrawable_stxoshi)}</div>
                        </div>
                        {(r.reasons || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {r.reasons.map((rs, i) => (
                              <Badge key={i} variant="outline" className="text-amber-400 border-amber-500/40 text-[10px]">{rs}</Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8"
                            disabled={processing === r.id + "approve"}
                            onClick={() => processReview(r, "approve")}
                            data-testid={`bots-review-approve-${r.id}`}>
                            {processing === r.id + "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            Send Full
                          </Button>
                          <div className="flex items-center gap-1">
                            <Input type="number" placeholder="amt" value={modifyAmt[r.id] || ""}
                              onChange={(e) => setModifyAmt({ ...modifyAmt, [r.id]: e.target.value })}
                              className="h-8 w-24 bg-zinc-800 border-zinc-700"
                              data-testid={`bots-review-amount-${r.id}`} />
                            <Button size="sm" variant="outline" className="h-8 border-zinc-700"
                              disabled={processing === r.id + "modify"}
                              onClick={() => processReview(r, "modify")}
                              data-testid={`bots-review-modify-${r.id}`}>
                              Modify & Send
                            </Button>
                          </div>
                          <Button size="sm" variant="outline" className="h-8 border-red-500/40 text-red-400"
                            disabled={processing === r.id + "decline"}
                            onClick={() => processReview(r, "decline")}
                            data-testid={`bots-review-decline-${r.id}`}>
                            <X className="w-3 h-3 mr-1" /> Cancel (Error)
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active bots risk board — mobile-friendly cards, no side-scroll */}
            <Card className="bg-zinc-900 border-zinc-800 mb-4" data-testid="admin-bots-active">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Active Bots — Risk Board</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[65vh] overflow-y-auto" data-testid="admin-bots-active-scroll">
                {(data?.active_bots || []).length === 0 && (
                  <p className="text-sm text-zinc-500 py-2">No active bots.</p>
                )}
                {[...(data?.active_bots || [])]
                  .sort((a, b) => (b.win_risk_stxoshi || 0) - (a.win_risk_stxoshi || 0))
                  .map((b) => (
                  <div key={b.id} className="border border-zinc-800 rounded-lg p-3" data-testid={`bots-active-row-${b.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <Link to={`/bot/${b.id}`} target="_blank" rel="noopener noreferrer"
                          className="font-medium text-orange-400 hover:underline inline-flex items-center gap-1"
                          data-testid={`bots-active-link-${b.id}`}>
                          {b.name || b.id.slice(0, 10)}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        <span className="text-[10px] text-zinc-500 ml-1">{b.bot_type}</span>
                        <button className="block text-[10px] text-zinc-500 font-mono mt-0.5 hover:text-zinc-300"
                          onClick={() => copyText(b.user_address)} title={b.user_address}
                          data-testid={`bots-active-owner-${b.id}`}>
                          owner …{(b.user_address || "").slice(-4)}
                        </button>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {b.admin_hold && <Badge className="bg-blue-600/20 text-blue-300 border-blue-500/40 text-[10px]">On Hold</Badge>}
                          {b.suspicious
                            ? <Badge className="bg-amber-600/20 text-amber-400 border-amber-500/40 text-[10px]" data-testid={`bots-flag-${b.id}`}>Suspicious</Badge>
                            : <Badge variant="outline" className="text-green-400 border-green-500/30 text-[10px]">OK</Badge>}
                          {Math.abs(b.principal_drift_stxoshi || 0) > 1_000_000 && (
                            <Badge
                              className={`text-[10px] ${b.drift_needs_review
                                ? "bg-purple-600/20 text-purple-300 border-purple-500/40"
                                : "bg-red-600/20 text-red-300 border-red-500/40"}`}
                              title={b.drift_needs_review
                                ? "Balance exceeds deposits + P&L — needs review"
                                : "Balance short vs deposit ledger — will auto-heal on next reconcile"}
                              data-testid={`bots-drift-${b.id}`}>
                              Drift {fmtMicro(b.principal_drift_stxoshi)}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className={`text-right font-semibold ${b.total_pnl_pct >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {b.total_pnl_pct >= 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> : <TrendingDown className="w-3 h-3 inline mr-0.5" />}
                        {fmtPct(b.total_pnl_pct)}
                        <div className="text-[10px] text-zinc-500 font-normal">{(b.days_held || 0).toFixed(1)}d</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                      <div><span className="text-zinc-500">Win risk</span> <span className="text-green-400">{fmtMicro(b.win_risk_stxoshi)}</span></div>
                      <div><span className="text-zinc-500">Loss risk</span> <span className="text-red-400">{fmtMicro(b.loss_risk_stxoshi)}</span></div>
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1" data-testid={`bots-active-opened-${b.id}`}>
                      Opened {fmtDate(b.opened_at)}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button size="sm" variant="outline" className="h-7 border-zinc-700 text-xs"
                        disabled={acting === b.id + "close"}
                        onClick={() => botAction(b, "close")} data-testid={`bots-close-${b.id}`}>
                        {acting === b.id + "close" ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3 mr-1" />} Close
                      </Button>
                      {b.admin_hold ? (
                        <Button size="sm" variant="outline" className="h-7 border-blue-500/40 text-blue-300 text-xs"
                          disabled={acting === b.id + "release_hold"}
                          onClick={() => botAction(b, "release_hold")} data-testid={`bots-release-${b.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Release Hold
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 border-blue-500/40 text-blue-300 text-xs"
                          disabled={acting === b.id + "hold"}
                          onClick={() => botAction(b, "hold")} data-testid={`bots-hold-${b.id}`}>
                          <Pause className="w-3 h-3 mr-1" /> Hold
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-7 border-amber-500/40 text-amber-300 text-xs"
                        disabled={acting === b.id + "close_principal"}
                        onClick={() => botAction(b, "close_principal")} data-testid={`bots-close-principal-${b.id}`}>
                        <DollarSign className="w-3 h-3 mr-1" /> Principal Only
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Closed bots ledger — mobile-friendly cards */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="admin-bots-closed">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Closed Bots</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="admin-bots-closed-scroll">
                {(data?.closed_bots || []).length === 0 && (
                  <p className="text-sm text-zinc-500 py-2">No closed bots.</p>
                )}
                {(data?.closed_bots || []).map((b) => (
                  <div key={b.id} className="flex items-center justify-between gap-2 border-b border-zinc-800/50 pb-2" data-testid={`bots-closed-row-${b.id}`}>
                    <div className="min-w-0">
                      <Link to={`/bot/${b.id}`} target="_blank" rel="noopener noreferrer"
                        className="font-medium text-orange-400 hover:underline inline-flex items-center gap-1"
                        data-testid={`bots-closed-link-${b.id}`}>
                        {b.name || b.id.slice(0, 10)} <ExternalLink className="w-3 h-3" />
                      </Link>
                      <span className="text-[10px] text-zinc-500 ml-1">{b.status}</span>
                      {b.suspicious && <Badge className="bg-amber-600/20 text-amber-400 border-amber-500/40 text-[10px] ml-1">Suspicious</Badge>}
                      <button className="block text-[10px] text-zinc-500 font-mono mt-0.5 hover:text-zinc-300"
                        onClick={() => copyText(b.user_address)} title={b.user_address}>
                        owner …{(b.user_address || "").slice(-4)}
                      </button>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="text-zinc-300">{fmtMicro(b.total_balance_stxoshi)}</div>
                      <div className="text-zinc-500">{(b.days_held || 0).toFixed(1)}d · <span className={b.total_pnl_pct >= 0 ? "text-green-400" : "text-red-400"}>{fmtPct(b.total_pnl_pct)}</span></div>
                      <div className="text-[10px] text-zinc-500 mt-0.5" data-testid={`bots-closed-opened-${b.id}`}>Opened {fmtDate(b.opened_at)}</div>
                      <div className="text-[10px] text-zinc-500" data-testid={`bots-closed-closed-${b.id}`}>Closed {fmtDate(b.closed_at)}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Sweep confirm dialog */}
      <Dialog open={sweepOpen} onOpenChange={setSweepOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Sweep Bots Treasury On-Chain</DialogTitle>
            <DialogDescription>
              This broadcasts a REAL STXOSHI transfer of the full sweepable balance
              ({fmtMicro(t.balance_stxoshi)} STXOSHI) from the hot wallet to the destination.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-zinc-500">Destination (leave blank for default WSY wallet)</label>
            <Input value={sweepDest} onChange={(e) => setSweepDest(e.target.value)}
              placeholder={data?.sweep_destination}
              className="bg-zinc-800 border-zinc-700 font-mono text-xs"
              data-testid="bots-sweep-dest-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSweepOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={doSweep} disabled={sweeping} className="bg-orange-600 hover:bg-orange-700" data-testid="bots-sweep-confirm-btn">
              {sweeping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Confirm Sweep
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
